import React, { useState, useEffect, useCallback } from 'react';
import { GitCompare, ArrowRight, AlertTriangle, RefreshCw } from 'lucide-react';
import { ContractSelector, ContractOption } from './ContractSelector';
import { ComparisonProgress, ComparisonStatus } from './ComparisonProgress';
import { LLMComparisonFlow } from './LLMComparisonFlow';
import { AnalysisHistorySidebar } from './AnalysisHistorySidebar';
import type { FlowStep } from './LLMComparisonFlow';
import {
  listLLMSessions,
  getLLMMatchingSession,
  deleteLLMSession,
  type LLMSessionSummary,
  type ArticleMatch,
  type LLMTOCEntry,
  type ArticleSectionMatches,
} from '../../services/api';

export interface ComparisonConfig {
  sourceContractId: string;
  targetContractId: string;
}

export interface ComparisonSelectionInterfaceProps {
  contracts: ContractOption[];
  onStartComparison: (config: ComparisonConfig) => void;
  onLLMComparisonComplete?: (sessionId: string) => void;
  comparisonStatus?: ComparisonStatus;
  comparisonProgress?: number;
  currentComparisonStep?: string;
  comparisonError?: string;
  onCancelComparison?: () => void;
  onRetryComparison?: () => void;
  isLoadingContracts?: boolean;
  contractsError?: string;
  testId?: string;
}

export const ComparisonSelectionInterface: React.FC<ComparisonSelectionInterfaceProps> = ({
  contracts,
  onStartComparison,
  onLLMComparisonComplete,
  comparisonStatus = 'idle',
  comparisonProgress = 0,
  currentComparisonStep,
  comparisonError,
  onCancelComparison,
  onRetryComparison,
  isLoadingContracts = false,
  contractsError,
  testId = 'comparison-selection',
}) => {
  const [sourceContractId, setSourceContractId] = useState('');
  const [targetContractId, setTargetContractId] = useState('');
  const [showLLMFlow, setShowLLMFlow] = useState(false);
  const [errors, setErrors] = useState<{
    source?: string;
    target?: string;
  }>({});

  // Sidebar state
  const [sessions, setSessions] = useState<LLMSessionSummary[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [sidebarActiveId, setSidebarActiveId] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Restored session state for LLMComparisonFlow
  const [restoredSessionId, setRestoredSessionId] = useState<string | undefined>();
  const [restoredTocMatches, setRestoredTocMatches] = useState<ArticleMatch[] | undefined>();
  const [restoredUnmatchedBaseline, setRestoredUnmatchedBaseline] = useState<LLMTOCEntry[] | undefined>();
  const [restoredUnmatchedTarget, setRestoredUnmatchedTarget] = useState<LLMTOCEntry[] | undefined>();
  const [restoredBaselineArticles, setRestoredBaselineArticles] = useState<LLMTOCEntry[] | undefined>();
  const [restoredTargetArticles, setRestoredTargetArticles] = useState<LLMTOCEntry[] | undefined>();
  const [restoredSectionMatches, setRestoredSectionMatches] = useState<ArticleSectionMatches[] | undefined>();
  const [restoredStep, setRestoredStep] = useState<FlowStep | undefined>();
  const [restoredTokensUsed, setRestoredTokensUsed] = useState<{ input: number; output: number } | undefined>();

  // Check if we have processed contracts available
  const processedStatuses = ['structured', 'embedded', 'linked', 'ready'];
  const processedContracts = contracts.filter((c) =>
    processedStatuses.includes(c.status)
  );
  const hasEnoughContracts = processedContracts.length >= 2;

  // Load session list on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await listLLMSessions();
        if (!cancelled) setSessions(result.sessions);
      } catch (err) {
        console.error('Failed to load LLM sessions:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Reset selection when contracts change
  useEffect(() => {
    if (!contracts.find((c) => c.id === sourceContractId)) {
      setSourceContractId('');
    }
    if (!contracts.find((c) => c.id === targetContractId)) {
      setTargetContractId('');
    }
  }, [contracts, sourceContractId, targetContractId]);

  const validateSelection = (): boolean => {
    const newErrors: { source?: string; target?: string } = {};

    if (!sourceContractId) {
      newErrors.source = 'Please select a source contract';
    }
    if (!targetContractId) {
      newErrors.target = 'Please select a target contract';
    }
    if (sourceContractId && targetContractId && sourceContractId === targetContractId) {
      newErrors.target = 'Target contract must be different from source';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStartComparison = () => {
    if (validateSelection()) {
      setShowLLMFlow(true);
      clearRestoredState();
    }
  };

  const clearRestoredState = () => {
    setRestoredSessionId(undefined);
    setRestoredTocMatches(undefined);
    setRestoredUnmatchedBaseline(undefined);
    setRestoredUnmatchedTarget(undefined);
    setRestoredBaselineArticles(undefined);
    setRestoredTargetArticles(undefined);
    setRestoredSectionMatches(undefined);
    setRestoredStep(undefined);
    setRestoredTokensUsed(undefined);
  };

  const handleLLMFlowBack = () => {
    setShowLLMFlow(false);
    setActiveSessionId(null);
    setSidebarActiveId(null);
    clearRestoredState();
  };

  const handleLLMFlowComplete = (sessionId: string) => {
    setShowLLMFlow(false);
    if (onLLMComparisonComplete) {
      onLLMComparisonComplete(sessionId);
    }
  };

  const handleReset = () => {
    setSourceContractId('');
    setTargetContractId('');
    setShowLLMFlow(false);
    setActiveSessionId(null);
    setSidebarActiveId(null);
    clearRestoredState();
    setErrors({});
  };

  // Build baseline/target article lists from TOC match data
  const buildArticleLists = (
    matches: ArticleMatch[],
    unmatchedBaseline: LLMTOCEntry[],
    unmatchedTarget: LLMTOCEntry[],
  ): { baselineArticles: LLMTOCEntry[]; targetArticles: LLMTOCEntry[] } => {
    const baselineArts: LLMTOCEntry[] = [];
    const seenBaseline = new Set<string>();

    matches.forEach((match) => {
      if (!seenBaseline.has(match.baselineArticleNumber)) {
        seenBaseline.add(match.baselineArticleNumber);
        baselineArts.push({
          articleNumber: match.baselineArticleNumber,
          articleTitle: match.baselineArticleTitle,
          sectionCount: 0,
        });
      }
    });

    unmatchedBaseline.forEach((entry) => {
      if (!seenBaseline.has(entry.articleNumber)) {
        seenBaseline.add(entry.articleNumber);
        baselineArts.push(entry);
      }
    });

    baselineArts.sort((a, b) => {
      const numA = parseInt(a.articleNumber.replace(/\D/g, '')) || 0;
      const numB = parseInt(b.articleNumber.replace(/\D/g, '')) || 0;
      return numA - numB;
    });

    const targetArts: LLMTOCEntry[] = [];
    const seenTargets = new Set<string>();

    matches.forEach((match) => {
      if (match.targetArticleNumber && !seenTargets.has(match.targetArticleNumber)) {
        seenTargets.add(match.targetArticleNumber);
        targetArts.push({
          articleNumber: match.targetArticleNumber,
          articleTitle: match.targetArticleTitle || '',
          sectionCount: 0,
        });
      }
    });

    unmatchedTarget.forEach((entry) => {
      if (!seenTargets.has(entry.articleNumber)) {
        seenTargets.add(entry.articleNumber);
        targetArts.push(entry);
      }
    });

    targetArts.sort((a, b) => {
      const numA = parseInt(a.articleNumber.replace(/\D/g, '')) || 0;
      const numB = parseInt(b.articleNumber.replace(/\D/g, '')) || 0;
      return numA - numB;
    });

    return { baselineArticles: baselineArts, targetArticles: targetArts };
  };

  // Handle selecting a session from the sidebar
  const handleSelectSession = useCallback(async (id: string) => {
    try {
      const response = await getLLMMatchingSession(id);
      const session = response.session;
      if (!session) {
        console.error('Session not found');
        return;
      }

      // Determine the step to restore to
      let step: FlowStep = 'toc_matching';
      if (session.section_matches && session.status === 'completed') {
        step = 'results';
      } else if (session.toc_matches) {
        step = 'review';
      }

      // Extract TOC match data
      const tocMatches = session.toc_matches?.matches ?? [];
      const unmatchedBaseline = session.toc_matches?.unmatchedBaseline ?? [];
      const unmatchedTarget = session.toc_matches?.unmatchedTarget ?? [];
      const sectionMatches = session.section_matches?.articlePairs ?? [];

      // Build article lists
      const { baselineArticles, targetArticles } = buildArticleLists(
        tocMatches,
        unmatchedBaseline,
        unmatchedTarget,
      );

      // Set contract IDs
      setSourceContractId(session.baseline_contract_id);
      setTargetContractId(session.target_contract_id);

      // Set restored state
      setRestoredSessionId(session.id);
      setRestoredTocMatches(tocMatches);
      setRestoredUnmatchedBaseline(unmatchedBaseline);
      setRestoredUnmatchedTarget(unmatchedTarget);
      setRestoredBaselineArticles(baselineArticles);
      setRestoredTargetArticles(targetArticles);
      setRestoredSectionMatches(sectionMatches);
      setRestoredStep(step);
      setRestoredTokensUsed({
        input: session.total_input_tokens ?? 0,
        output: session.total_output_tokens ?? 0,
      });

      // Show the flow and set sidebar active
      setActiveSessionId(session.id);
      setSidebarActiveId(session.id);
      setShowLLMFlow(true);
    } catch (err) {
      console.error('Failed to load session:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle new analysis from sidebar
  const handleNewAnalysis = useCallback(() => {
    setShowLLMFlow(false);
    setActiveSessionId(null);
    setSidebarActiveId(null);
    clearRestoredState();
    setSourceContractId('');
    setTargetContractId('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle delete session from sidebar
  const handleDeleteSession = useCallback(async (id: string) => {
    try {
      await deleteLLMSession(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      if (activeSessionId === id) {
        handleNewAnalysis();
      }
    } catch (err) {
      console.error('Failed to delete session:', err);
    }
  }, [activeSessionId, handleNewAnalysis]);

  // Called when a new session is created during TOC matching
  const handleSessionCreated = useCallback((newSessionId: string) => {
    setSidebarActiveId(newSessionId);
    setActiveSessionId(newSessionId);
    // Add the new session to the top of the list
    const newSummary: LLMSessionSummary = {
      id: newSessionId,
      baselineContractId: sourceContractId,
      targetContractId: targetContractId,
      status: 'awaiting_review',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setSessions((prev) => [newSummary, ...prev.filter((s) => s.id !== newSessionId)]);
  }, [sourceContractId, targetContractId]);

  const isComparing = ['preparing', 'comparing', 'analyzing'].includes(comparisonStatus);
  const isDisabled = isComparing || isLoadingContracts;

  // Get selected contracts for display
  const sourceContract = contracts.find((c) => c.id === sourceContractId);
  const targetContract = contracts.find((c) => c.id === targetContractId);

  // If LLM flow is active, render sidebar + flow side by side
  if (showLLMFlow && sourceContractId && targetContractId) {
    return (
      <div className="flex rounded-lg border border-secondary-200 overflow-hidden" style={{ height: 'calc(100vh - 12rem)' }} data-testid={`${testId}-llm-layout`}>
        <AnalysisHistorySidebar
          sessions={sessions}
          activeSessionId={sidebarActiveId}
          onSelectSession={handleSelectSession}
          onNewAnalysis={handleNewAnalysis}
          onDeleteSession={handleDeleteSession}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
          contracts={contracts}
        />
        <div className="flex-1 min-w-0 overflow-y-auto p-6">
          <LLMComparisonFlow
            key={activeSessionId ?? 'new'}
            contracts={contracts}
            sourceContractId={sourceContractId}
            targetContractId={targetContractId}
            onBack={handleLLMFlowBack}
            onComplete={handleLLMFlowComplete}
            onSessionCreated={handleSessionCreated}
            initialSessionId={restoredSessionId}
            initialTocMatches={restoredTocMatches}
            initialUnmatchedBaseline={restoredUnmatchedBaseline}
            initialUnmatchedTarget={restoredUnmatchedTarget}
            initialBaselineArticles={restoredBaselineArticles}
            initialTargetArticles={restoredTargetArticles}
            initialSectionMatches={restoredSectionMatches}
            initialStep={restoredStep}
            initialTokensUsed={restoredTokensUsed}
            testId={`${testId}-llm-flow`}
          />
        </div>
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <div className="space-y-6">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-6" data-testid={`${testId}-header`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <GitCompare className="h-6 w-6 text-primary-600 mr-3" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900" data-testid={`${testId}-title`}>
                    Contract Comparison
                  </h2>
                  <p className="text-sm text-gray-500" data-testid={`${testId}-subtitle`}>
                    Compare two contracts to identify gaps, differences, and recommendations
                  </p>
                </div>
              </div>
              {(sourceContractId || targetContractId) && !isComparing && (
                <button
                  type="button"
                  onClick={handleReset}
                  className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
                  data-testid={`${testId}-reset`}
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Reset
                </button>
              )}
            </div>

            {/* Loading state */}
            {isLoadingContracts && (
              <div
                className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center"
                data-testid={`${testId}-loading`}
              >
                <RefreshCw className="h-5 w-5 text-blue-600 mr-3 animate-spin" />
                <p className="text-sm text-blue-700">Loading contracts...</p>
              </div>
            )}

            {/* Error loading contracts */}
            {contractsError && (
              <div
                className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start"
                data-testid={`${testId}-contracts-error`}
              >
                <AlertTriangle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Failed to load contracts</p>
                  <p className="text-sm text-red-700 mt-1">{contractsError}</p>
                </div>
              </div>
            )}

            {/* Warning if not enough contracts */}
            {!isLoadingContracts && !contractsError && !hasEnoughContracts && (
              <div
                className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start"
                data-testid={`${testId}-warning`}
              >
                <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">
                    Not enough processed contracts
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    You need at least 2 processed contracts (status: structured, embedded, linked, or
                    ready) to perform a comparison. Currently you have {processedContracts.length}.
                  </p>
                </div>
              </div>
            )}

            {/* Contract Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Source Contract */}
              <ContractSelector
                label="Source Contract (Baseline)"
                contracts={contracts}
                selectedContractId={sourceContractId}
                onSelect={(id) => {
                  setSourceContractId(id);
                  setErrors((prev) => ({ ...prev, source: undefined }));
                }}
                excludeContractId={targetContractId}
                placeholder="Select source contract..."
                testId={`${testId}-source`}
                disabled={isDisabled || !hasEnoughContracts}
                error={errors.source}
              />

              {/* Arrow indicator */}
              <div className="hidden md:flex items-center justify-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <ArrowRight className="h-6 w-6 text-gray-300" />
              </div>

              {/* Target Contract */}
              <ContractSelector
                label="Target Contract (Compare Against)"
                contracts={contracts}
                selectedContractId={targetContractId}
                onSelect={(id) => {
                  setTargetContractId(id);
                  setErrors((prev) => ({ ...prev, target: undefined }));
                }}
                excludeContractId={sourceContractId}
                placeholder="Select target contract..."
                testId={`${testId}-target`}
                disabled={isDisabled || !hasEnoughContracts}
                error={errors.target}
              />
            </div>

            {/* Selected Summary */}
            {sourceContract && targetContract && !isComparing && (
              <div
                className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200"
                data-testid={`${testId}-summary`}
              >
                <h4 className="text-sm font-medium text-gray-700 mb-2">Comparison Summary</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>
                    <span className="font-medium">Source:</span> {sourceContract.name} ({sourceContract.state})
                  </p>
                  <p>
                    <span className="font-medium">Target:</span> {targetContract.name} ({targetContract.state})
                  </p>
                  <p>
                    <span className="font-medium">Type:</span>{' '}
                    AI-Guided Matching with Human Review
                  </p>
                </div>
              </div>
            )}

            {/* Start Comparison Button */}
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={handleStartComparison}
                disabled={isDisabled || !hasEnoughContracts}
                className={`
                  inline-flex items-center px-6 py-3 rounded-lg font-medium text-white
                  transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
                  ${
                    isDisabled || !hasEnoughContracts
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-primary-600 hover:bg-primary-700'
                  }
                `}
                data-testid={`${testId}-start-button`}
              >
                <GitCompare className="h-5 w-5 mr-2" />
                {isComparing ? 'Comparison in Progress...' : 'Start Comparison'}
              </button>
            </div>
          </div>

          {/* Progress Section */}
          {comparisonStatus !== 'idle' && (
            <ComparisonProgress
              status={comparisonStatus}
              progress={comparisonProgress}
              currentStep={currentComparisonStep}
              errorMessage={comparisonError}
              onRetry={onRetryComparison}
              onCancel={onCancelComparison}
              testId={`${testId}-progress`}
            />
          )}
        </div>

        {/* Previous sessions link */}
        {sessions.length > 0 && (
          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                // Show the most recent session
                handleSelectSession(sessions[0].id);
              }}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              View {sessions.length} previous {sessions.length === 1 ? 'analysis' : 'analyses'}
            </button>
          </div>
        )}
      </div>
  );
};

export default ComparisonSelectionInterface;
